import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

// SVG cubic Bezier: start (x1,y1), control points (cx1,y1) and (cx2,y2), end (x2,y2). Horizontal tangents at the ends keep the curve reading as a left-to-right connector.
const buildPath = (n: number[]): string => {
  return `M ${n[0]} ${n[1]} C ${n[2]} ${n[3]}, ${n[4]} ${n[5]}, ${n[6]} ${n[7]}`;
};

// Smoothstep-style easing so the line eases in and out instead of linear sliding.
const easeInOut = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

// Draws an animated curve between the selected song row and selected voice pill. Refs come from the parent; positions are read from getBoundingClientRect in container-local space so the SVG overlay stays aligned on scroll and resize.
export function ConversionShowcaseConnector({
  fromRefs,
  toRefs,
  containerRef,
  selectedSongLabel,
  selectedVoiceId,
  animKey,
}: {
  fromRefs: RefObject<Map<string, HTMLElement>>;
  toRefs: RefObject<Map<string, HTMLElement>>;
  containerRef: RefObject<HTMLDivElement | null>;
  selectedSongLabel: string;
  selectedVoiceId: string;
  animKey: string;
}) {
  // Drives SVG width/height so the coordinate system matches the layout box.
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  const activePathRef = useRef<SVGPathElement | null>(null);
  // Last fully drawn endpoint/control tuple; interpolation starts here when selection changes.
  const currentNums = useRef<number[] | null>(null);
  const rafRef = useRef<number | null>(null);

  const computeNums = useCallback((): number[] | null => {
    const c = containerRef.current;
    const f = fromRefs.current?.get(selectedSongLabel) ?? null;
    const t = toRefs.current?.get(selectedVoiceId) ?? null;
    if (c === null || f === null || t === null) {
      return null;
    }

    const cRect = c.getBoundingClientRect();
    const fRect = f.getBoundingClientRect();
    const tRect = t.getBoundingClientRect();

    // Small horizontal padding so the stroke does not sit flush on the buttons.
    const x1 = fRect.right - cRect.left + 6;
    const y1 = fRect.top - cRect.top + fRect.height / 2;
    const x2 = tRect.left - cRect.left - 6;
    const y2 = tRect.top - cRect.top + tRect.height / 2;
    // Control points pulled toward the middle of the span for a gentle S-like bend.
    const cx1 = x1 + (x2 - x1) * 0.45;
    const cx2 = x2 - (x2 - x1) * 0.45;

    return [x1, y1, cx1, y1, cx2, y2, x2, y2];
  }, [containerRef, fromRefs, toRefs, selectedSongLabel, selectedVoiceId]);

  const setActivePath = (d: string) => {
    activePathRef.current?.setAttribute("d", d);
  };

  // Keep SVG dimensions in sync when the flex layout or viewport changes.
  useEffect(() => {
    const el = containerRef.current;
    if (el === null) {
      return;
    }
    const update = () =>
      setContainerSize({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  // On resize (or when refs resolve), jump the path to the correct geometry without animating. Without this, the curve could stay wrong until the user changes selection again.
  useEffect(() => {
    const nums = computeNums();
    if (nums === null) {
      return;
    }
    if (currentNums.current === null) {
      currentNums.current = nums;
    }
    setActivePath(buildPath(nums));
  }, [computeNums, containerSize]);

  // When song/voice selection changes (animKey), tween from the previous curve to the new one.
  useEffect(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    const targetNums = computeNums();
    if (targetNums === null) {
      return;
    }

    const fromNums =
      currentNums.current !== null ? [...currentNums.current] : targetNums;

    const DURATION = 360;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const raw = Math.min(elapsed / DURATION, 1);
      const t = easeInOut(raw);

      const interp = fromNums.map((v, i) => v + (targetNums[i] - v) * t);
      setActivePath(buildPath(interp));

      if (raw < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        currentNums.current = targetNums;
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [animKey, computeNums]);

  // Avoid a zero-size SVG flash before the first ResizeObserver tick.
  if (containerSize.w === 0) {
    return null;
  }

  return (
    <svg
      aria-hidden
      style={{
        position: "absolute",
        pointerEvents: "none",
        left: 0,
        top: 0,
        width: containerSize.w,
        height: containerSize.h,
        overflow: "visible",
        zIndex: 0,
      }}
    >
      <path
        ref={activePathRef}
        className="text-gray-900"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeLinecap="round"
      />
    </svg>
  );
}
